import { overlayScale, scaleAlpha } from "../../lib/overlay-darkness";

type HeroOverlay = "dark" | "light" | "gradient" | "brand";
type HeroOverlayStrength = "light" | "medium" | "strong";

/** One colour stop: the colour, how opaque at Medium, and where it sits along the gradient. */
interface OverlayStop {
  color: string;
  alpha: number;
  at: number;
}

interface OverlayPreset {
  direction: string;
  stops: readonly OverlayStop[];
}

/** A colour-mix percentage is an opacity times this. */
const PERCENT_OF_ONE = 100;

/** The site's primary colour, so Brand follows the theme. */
const BRAND_COLOR = "var(--jobsite-primary)";

const PRESETS: Record<HeroOverlay, OverlayPreset> = {
  brand: {
    direction: "to top",
    stops: [
      { alpha: 0.85, at: 0, color: BRAND_COLOR },
      { alpha: 0.7, at: 50, color: BRAND_COLOR },
      { alpha: 0.5, at: 100, color: BRAND_COLOR },
    ],
  },
  dark: {
    direction: "to top",
    stops: [
      { alpha: 0.74, at: 0, color: "0, 0, 0" },
      { alpha: 0.55, at: 45, color: "0, 0, 0" },
      { alpha: 0.3, at: 100, color: "0, 0, 0" },
    ],
  },
  gradient: {
    direction: "135deg",
    stops: [
      { alpha: 0.86, at: 0, color: "4, 24, 17" },
      { alpha: 0.72, at: 55, color: "18, 73, 44" },
      { alpha: 0.42, at: 100, color: "0, 0, 0" },
    ],
  },
  light: {
    direction: "to right",
    stops: [
      { alpha: 0.95, at: 0, color: "255, 255, 255" },
      { alpha: 0.7, at: 50, color: "255, 255, 255" },
      { alpha: 0.3, at: 100, color: "255, 255, 255" },
    ],
  },
};

/**
 * A stop's CSS at a strength: rgba for fixed colours, color-mix for the theme's.
 * @param {OverlayStop} stop The stop at Medium.
 * @param {number} scale The strength's multiplier.
 * @returns {string} The CSS colour stop.
 */
const stopCss = (stop: OverlayStop, scale: number): string => {
  const alpha = scaleAlpha(stop.alpha, scale);
  const color = stop.color === BRAND_COLOR
    ? `color-mix(in srgb, ${BRAND_COLOR} ${Math.round(alpha * PERCENT_OF_ONE)}%, transparent)`
    : `rgba(${stop.color}, ${alpha})`;
  return `${color} ${stop.at}%`;
};

/**
 * The hero's overlay over its photo, as a CSS background-image.
 * @param {HeroOverlay | undefined} overlay The overlay style; Dark when unset.
 * @param {number | undefined} darkness The darkness slider, 0 to 100; the designed look when unset.
 * @returns {string} A linear-gradient.
 */
const heroOverlayGradient = (overlay?: HeroOverlay, darkness?: number): string => {
  const preset = PRESETS[overlay ?? "dark"];
  const scale = overlayScale(darkness);
  return `linear-gradient(${preset.direction}, ${preset.stops.map((stop) => stopCss(stop, scale)).join(", ")})`;
};

export type { HeroOverlay, HeroOverlayStrength };
export { heroOverlayGradient };
